import { useState, useEffect } from 'react';
import OtpInput from 'react-otp-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser, confirmEmailSignUp, resendEmailConfirmation } from '../store/authSlice';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import NavBarPhone from './NavBarPhone/NavBarPhone';
import { getAndClearRedirectPath, getAuthRedirectPath } from '../util/redirectUtils';
import { mapCognitoErrorToGerman } from '../util/authErrorMessages';
import { trackLogin } from '../util/tracking';
import { notifySuccessToast } from '../util/notifyActionError';

import classes from "./SignInPhones.module.css";
import SocialForm from './UI/SocialForm';
import GermanFlag from "../public/HomepagePhotos/germanFlag.svg";
import backArrow from "../public/icons/ZurückIcon.svg";

const OtpChecker = ({ telephone, onOtpSubmit }) => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();

    const correctOtp = '123456';

    if (!otp) {
      setOtpError('Code muss ausgefüllt werden');
    } else if (otp !== correctOtp) {
      setOtpError('Code ist nicht korrekt');
    } else {
      setOtpError('');
      onOtpSubmit();
    }
  };

  return (
    <>
        <div className={classes.telephoneContainer}>
          <div className={classes.telephoneInfosContainer}>
            <span className='regularLarge'>
              Gib den 6-stelligen Code ein, den wir an +49 {telephone} gesendet haben.
            </span>
          </div>
          <div className={classes.otpInput} style={{ marginBottom: otpError ? '15px' : '30px'}}>
            <OtpInput
              value={otp}
              onChange={(otp) => { setOtp(otp); setOtpError(''); }}
              numInputs={6}
              renderInput={(props) => <input {...props} />}
            />
          </div>
          {otpError && 
            <div className={classes.errorContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
              </svg>
              <span className='slimRegularSmallText'>{otpError}</span>
            </div>
          }
          <button className={classes.buttonWeiter} type="submit" onClick={handleSubmit}>
            Weiter
          </button>
          <div className={classes.keinSmsComponent}>
            <span>Keine SMS erhalten? </span>
            <span className={classes.underlineSpan}>Erneut senden</span>
          </div>
        </div>
    </>
  );
};

const getAuthErrorMessage = (error, fallback) => (
  typeof error === 'string' ? error : mapCognitoErrorToGerman(error, fallback)
);

export const SignInPhone = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const isAuthLoading = auth.status === 'loading';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telephone, setTelephone] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showTelephoneInput, setShowTelephoneInput] = useState(false);
  const [telephoneSubmitted, setTelephoneSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSocialForm, setResetSocialForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCodeFocused, setIsCodeFocused] = useState(false);
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [forgotPasswordConfirmationCode, setForgotPasswordConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Email confirmation states
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [emailConfirmationCode, setEmailConfirmationCode] = useState('');
  const [resendingCode, setResendingCode] = useState(false);

  // Handle successful authentication with proper redirect logic
  useEffect(() => {
    if (auth.status === 'succeeded' && auth.isAuthenticated) {
      // Prefer any explicitly stored redirect intent
      const hasExplicit = sessionStorage.getItem('oauth_redirect_path');
      if (hasExplicit) {
        const target = getAndClearRedirectPath();
        navigate(target || '/', { replace: true });
        return;
      }

      navigate(getAuthRedirectPath(location), { replace: true });
    } else if (auth.status === 'failed') {
      setError(getAuthErrorMessage(auth.error));
    } else if (auth.status === 'succeeded' && auth.requiresEmailConfirmation) {
      setShowEmailConfirmation(true);
      setEmailSubmitted(false);
      setShowTelephoneInput(false);
      setShowForgotPassword(false);
      setShowResetConfirmation(false);
      setTelephoneSubmitted(false);
      setError('');
    }
  }, [auth.status, auth.isAuthenticated, auth.error, auth.requiresEmailConfirmation, navigate, location]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (showTelephoneInput) {
      if (!telephone) {
        setError('Telefonnummer muss ausgefüllt werden');
      } else {
        setError('');
        setTelephoneSubmitted(true);
      }
    } else {
      if (!emailSubmitted) {
        if (!email) {
          setError('E-Mail Adresse muss ausgefüllt werden');
        } else if (!validateEmail(email)) {
          setError('E-Mail Adresse ist nicht gültig');
        } else {
          setError('');
          setEmailSubmitted(true);
        }
      } else {
        if (!password) {
          setError('Passwort muss ausgefüllt werden');
        } else {
          setError('');
          // Email/password flow – localize any Cognito / Amplify errors
          try {
            setIsSubmitting(true);
            const loginResult = await dispatch(loginUser({ email, password })).unwrap();
            if (loginResult?.requiresEmailConfirmation) {
              return;
            }

            trackLogin({
              method: 'email',
              source: 'login_page',
            });

            const explicit = sessionStorage.getItem('oauth_redirect_path');
            if (explicit) {
              const target = getAndClearRedirectPath();
              navigate(target || '/', { replace: true });
              return;
            }
          } catch (error) {
            const germanMessage = getAuthErrorMessage(error);
            setError(germanMessage);
          } finally {
            setIsSubmitting(false);
          }
        }
      }
    }
  };

  // Optional: keep this if you later add a dedicated loginError field
  // useEffect(() => {
  //   if (auth?.loginError) {
  //     setError(mapCognitoErrorToGerman(auth?.loginError));
  //   }
  // }, [auth?.loginError]);

  const handleBackToTelephone = () => {
    setTelephoneSubmitted(false);
    setResetSocialForm(true);
  };

  const handleTelephoneLogin = () => {
    setError("")
    setShowTelephoneInput(prevState => !prevState);
  };

  const handleOtpSubmit = () => {
    trackLogin({
      method: 'phone',
      source: 'login_page',
    });
    navigate(getAuthRedirectPath(location), { replace: true });
  };

  const handleSwitchToRegister = () => {
    navigate('/registrieren', { state: location.state });
  };

  const handleCodeFocus = () => {
    setIsCodeFocused(true);
  };

  const handleCodeBlur = (e) => {
    if (!e.target.value) {
      setIsCodeFocused(false);
    }
  };

  const handleNewPasswordFocus = () => {
    setIsNewPasswordFocused(true);
  };

  const handleNewPasswordBlur = (e) => {
    if (!e.target.value) {
      setIsNewPasswordFocused(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setError('');
    setShowForgotPassword(true);
    setShowResetConfirmation(false);
    setShowEmailConfirmation(false);
    setForgotPasswordEmail(email || '');
    setEmailSubmitted(false);
    setShowTelephoneInput(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      setError('E-Mail Adresse muss ausgefüllt werden');
      return;
    }

    if (!validateEmail(forgotPasswordEmail)) {
      setError('E-Mail Adresse ist nicht gültig');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);
      const output = await resetPassword({ username: forgotPasswordEmail });
      const { nextStep } = output || {};
      if (nextStep?.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        setShowResetConfirmation(true);
      }
    } catch (error) {
      const germanMessage = getAuthErrorMessage(error);
      setError(germanMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!forgotPasswordConfirmationCode) {
      setError('Bestätigungscode muss ausgefüllt werden');
      return;
    }

    if (!newPassword) {
      setError('Neues Passwort muss ausgefüllt werden');
      return;
    }

    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);

      await confirmResetPassword({
        username: forgotPasswordEmail,
        confirmationCode: forgotPasswordConfirmationCode,
        newPassword
      });

      setShowForgotPassword(false);
      setShowResetConfirmation(false);
      setForgotPasswordEmail('');
      setForgotPasswordConfirmationCode('');
      setNewPassword('');
      setError('');
      notifySuccessToast('Passwort erfolgreich zurückgesetzt! Sie können sich jetzt mit Ihrem neuen Passwort anmelden.', 'Passwort zurückgesetzt');
    } catch (error) {
      const germanMessage = getAuthErrorMessage(error);
      setError(germanMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForgotPassword = () => {
    setShowResetConfirmation(false);
    setForgotPasswordConfirmationCode('');
    setNewPassword('');
    setError('');
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
    setShowResetConfirmation(false);
    setShowEmailConfirmation(false);
    setForgotPasswordEmail('');
    setForgotPasswordConfirmationCode('');
    setNewPassword('');
    setError('');
  };

  const handleEmailConfirmationSubmit = async (e) => {
    e.preventDefault();

    if (!emailConfirmationCode) {
      setError('Bestätigungscode muss ausgefüllt werden');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);

      await dispatch(confirmEmailSignUp({
        email: auth.emailToConfirm || email,
        confirmationCode: emailConfirmationCode
      })).unwrap();

      setShowEmailConfirmation(false);
      setEmailConfirmationCode('');
      setEmailSubmitted(false);
      setError('');

      notifySuccessToast('E-Mail erfolgreich bestätigt! Sie können sich jetzt mit Ihrem Passwort anmelden.', 'E-Mail bestätigt');
    } catch (error) {
      setError(getAuthErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmailConfirmation = async () => {
    try {
      setResendingCode(true);
      setError('');

      await dispatch(resendEmailConfirmation({
        email: auth.emailToConfirm || email
      })).unwrap();

      notifySuccessToast('Bestätigungscode wurde erneut gesendet!', 'Code gesendet');
    } catch (error) {
      setError(getAuthErrorMessage(error, 'Fehler beim Senden des Codes'));
    } finally {
      setResendingCode(false);
    }
  };

  const handleBackFromEmailConfirmation = () => {
    setShowEmailConfirmation(false);
    setEmailConfirmationCode('');
    setError('');
  };

  return (
    <div className={classes.signInPhoneContainer}>
      {(telephoneSubmitted || showResetConfirmation || showEmailConfirmation) && (
        <div className={classes.backEmailContainer} onClick={
          showResetConfirmation ? handleBackToForgotPassword :
          showEmailConfirmation ? handleBackFromEmailConfirmation :
          handleBackToTelephone
        }>
          <img src={backArrow} alt='back to email' />
          <span>Zurück</span>
        </div>
      )}
      {showForgotPassword && !showResetConfirmation && (
        <div className={classes.backEmailContainer} onClick={handleBackToSignIn}>
          <img src={backArrow} alt='back to sign in' />
          <span>Zurück</span>
        </div>
      )}
      <div className={classes.modalContainer}>
        <h1 className='titleSemibold'>
          {telephoneSubmitted
            ? 'Nummer bestätigen'
            : showEmailConfirmation
              ? 'E-Mail bestätigen'
            : showForgotPassword
              ? (showResetConfirmation ? 'Passwort zurücksetzen' : 'Passwort vergessen')
              : 'Anmelden'}
        </h1>
        {!telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            {showTelephoneInput ? (
              <div className={classes.phoneInputContainer} style={{ marginBottom: error ? '15px' : '30px'}}>
                <div className={classes.countryFlagContainer}>
                  <img src={GermanFlag} alt='Germany Country Telephone Code' />
                </div>
                <span className={classes.countryCode}>+49</span>
                <div className={classes.smallLine}></div>
                <input
                  className='slimRegularLarge'
                  type="number"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className={`${classes.phoneLabel} ${isFocused ? classes.phoneFocused : ''}`}>
                  Telefonnummer eingeben
                </label>
              </div>
            ) : (
              <>
                <div className={classes.inputContainer} style={{ marginBottom: error ? '15px' : '30px'}}>
                  <label 
                    className={`${classes.label} 
                    ${isFocused || email ? classes.focused : ''}`}
                  >
                    E-Mail eingeben
                  </label>
                  <input
                    type="email"
                    className='slimRegularLarge'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
                {emailSubmitted && (
                  <div className={classes.inputContainer} style={{ marginBottom: error ? '0.906rem' : '1.875rem', position: 'relative' }}>
                    <label className={`${classes.label} ${isFocused || password ? classes.focused : ''}`}>
                      Passwort eingeben
                    </label>
                    <input
                      className={classes.passwordInput}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    <button
                      type="button"
                      className={`${classes.showPasswordButton} regularXs`}
                      onClick={() => setShowPassword(prev => !prev)}
                    >
                      {showPassword ? "Verbergen" : "Anzeigen"}
                    </button>
                  </div>
                )}
              </>
            )}
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button 
              style={{ marginBottom: emailSubmitted ? '.5rem' : '1rem'}} 
              className={`${classes.submitButton} mediumLarge ${(isAuthLoading || isSubmitting) ? classes.submitButtonLoading : ''}`} 
              type="submit"
              disabled={isAuthLoading || isSubmitting}
            >
              {(isAuthLoading || isSubmitting) ? 'Wird überprüft...' : 'Weiter'}
            </button>
          </form>
        )}
        {showForgotPassword && !showResetConfirmation && (
          <form className={classes.form} onSubmit={handleForgotPasswordSubmit} noValidate>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '15px' : '30px' }}>
              <label className={`${classes.label} ${isFocused || forgotPasswordEmail ? classes.focused : ''}`}>
                E-Mail eingeben
              </label>
              <input
                type="email"
                className='slimRegularLarge'
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button
              className={`${classes.submitButton} mediumLarge ${isSubmitting ? classes.submitButtonLoading : ''}`}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird gesendet...' : 'Code senden'}
            </button>
          </form>
        )}
        {showForgotPassword && showResetConfirmation && (
          <form className={classes.form} onSubmit={handleResetPasswordSubmit} noValidate>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '15px' : '20px' }}>
              <label className={`${classes.label} ${isCodeFocused || forgotPasswordConfirmationCode ? classes.focused : ''}`}>
                Bestätigungscode eingeben
              </label>
              <input
                type="text"
                className='slimRegularLarge'
                value={forgotPasswordConfirmationCode}
                onChange={(e) => setForgotPasswordConfirmationCode(e.target.value)}
                onFocus={handleCodeFocus}
                onBlur={handleCodeBlur}
                maxLength={6}
              />
            </div>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '15px' : '30px', position: 'relative' }}>
              <label className={`${classes.label} ${isNewPasswordFocused || newPassword ? classes.focused : ''}`}>
                Neues Passwort eingeben
              </label>
              <input
                className={classes.passwordInput}
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={handleNewPasswordFocus}
                onBlur={handleNewPasswordBlur}
              />
              <button
                type="button"
                className={`${classes.showPasswordButton} regularXs`}
                onClick={() => setShowNewPassword(prev => !prev)}
              >
                {showNewPassword ? 'Verbergen' : 'Anzeigen'}
              </button>
            </div>
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button
              className={`${classes.buttonWeiter} ${isSubmitting ? classes.submitButtonLoading : ''}`}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
            </button>
          </form>
        )}
        {showEmailConfirmation && (
          <form className={classes.form} onSubmit={handleEmailConfirmationSubmit} noValidate>
            <div className={classes.telephoneInfosContainer}>
              <span className='regularLarge'>
                Gib den 6-stelligen Code ein, den wir an {auth.emailToConfirm || email} gesendet haben.
              </span>
            </div>
            <div className={classes.spamContainer}>
              <span className='regularSmall'>📩 Auch im SPAM-Ordner nachschauen!</span>
            </div>
            <div className={classes.otpInput} style={{ marginBottom: error ? '15px' : '30px'}}>
              <OtpInput
                value={emailConfirmationCode}
                onChange={(code) => { setEmailConfirmationCode(code); setError(''); }}
                numInputs={6}
                renderInput={(props) => {
                  const handleKeyDown = (e) => {
                    if (e.key === 'Backspace' && e.currentTarget.value) {
                      e.stopPropagation();
                    }
                    if (props.onKeyDown) {
                      props.onKeyDown(e);
                    }
                  };

                  return <input {...props} inputMode="numeric" autoComplete="one-time-code" onKeyDown={handleKeyDown} />;
                }}
                shouldAutoFocus
              />
            </div>
            {error && (
              <div className={classes.errorContainer}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span className='slimRegularSmallText'>{error}</span>
              </div>
            )}
            <button
              className={`${classes.submitButton} mediumLarge ${isSubmitting ? classes.submitButtonLoading : ''}`}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird bestätigt...' : 'E-Mail bestätigen'}
            </button>
            <div className={classes.keinSmsComponent}>
              <span>Keine E-Mail erhalten? </span>
              <button
                type="button"
                onClick={handleResendEmailConfirmation}
                disabled={resendingCode}
                className={classes.underlineSpan}
              >
                {resendingCode ? 'Wird gesendet...' : 'Erneut senden'}
              </button>
            </div>
          </form>
        )}
        {telephoneSubmitted && <OtpChecker telephone={telephone} onOtpSubmit={handleOtpSubmit} />}
        {emailSubmitted && !showTelephoneInput && !telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <div className={classes.forgotPasswordContainer}>
            <button
              type="button"
              onClick={handleForgotPasswordClick}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: 0
              }}
            >
              Passwort vergessen?
            </button>
          </div>
        )}
        {!telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <>
            <div className={classes.oderContainer}>
              <div></div>
              <span>oder</span>
              <div></div>
            </div>
            <SocialForm onTelephoneLogin={handleTelephoneLogin} resetFirstBoxClicked={resetSocialForm} />
            <div className={classes.registerContainer}>
              <span>Noch kein Konto?</span>
              <button onClick={handleSwitchToRegister}>Hier registrieren</button>
            </div>
          </>
        )}
      </div>
      <NavBarPhone fromSignIn={true} />
    </div>
  );
};

export default SignInPhone;
